import { faBoxArchive, faPen } from '@fortawesome/pro-light-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useQueryClient } from '@tanstack/react-query';
import { createFileRoute } from '@tanstack/react-router';
import { useState } from 'react';
import { toast } from 'sonner';
import { fetchAdminAttachmentTemplate } from '@/api/orders';
import { AttachmentTemplateRules } from '@/components/shared/AttachmentTemplateRules';
import {
  PageHeader,
  PageHeaderFooter,
  PageHeaderFooterLeft,
  PageHeaderFooterRight,
} from '@/components/shared/PageHeader';
import { QueryErrorState } from '@/components/shared/QueryErrorState';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Button } from '@/components/ui/button';
import type { FacilityProcessGroupOption } from '@/hooks/useGroupedFacilityProcessOptions';
import { useGroupedFacilityProcessOptions } from '@/hooks/useGroupedFacilityProcessOptions';
import { usePermissions } from '@/hooks/usePermissions';
import { useTranslator } from '@/hooks/useTranslator';
import { ATTACHMENT_TEMPLATE_TRACING_TYPES } from '@/lib/constants/attachmentTemplate';
import { COUNTRY_OPTIONS } from '@/lib/constants/countryOptions';
import { queryClient } from '@/lib/utils/queryClient';
import { useSessionStore } from '@/store/sessionStore';
import { useAdminAttachmentTemplate } from '../../../-hooks/useAdminAttachmentTemplate';
import { useArchiveAdminAttachmentTemplate } from '../../../-hooks/useArchiveAdminAttachmentTemplate';

export const Route = createFileRoute(
  '/(client)/orders/(pages)/attachment-templates/$id/',
)({
  loader: ({ params }) =>
    queryClient.ensureQueryData({
      queryKey: ['orders', 'adminAttachmentTemplate', params.id],
      queryFn: () => fetchAdminAttachmentTemplate(params.id),
    }),
  component: AttachmentTemplateDetailPage,
});

function AttachmentTemplateDetailPage() {
  const { id } = Route.useParams();
  const navigate = Route.useNavigate();
  const { getTranslation: t } = useTranslator({ page: 'attachmentTemplates' });
  const qc = useQueryClient();
  const sessionUser = useSessionStore(s => s.user);
  const [archiveOpen, setArchiveOpen] = useState(false);

  const { data, error, refetch } = useAdminAttachmentTemplate(id);
  const archiveMutation = useArchiveAdminAttachmentTemplate();
  const { data: fpGroups = [] } = useGroupedFacilityProcessOptions();
  const countryOptions = COUNTRY_OPTIONS;
  const orderPerms = usePermissions().orders;

  const fpLabelById = new Map<string, string>(
    (fpGroups as FacilityProcessGroupOption[]).flatMap(g =>
      g.options.map(o => [o.key, o.label]),
    ),
  );
  const countryNameByCode = new Map(countryOptions.map(c => [c.key, c.name]));

  if (error || !data) {
    return <QueryErrorState error={error} onRetry={() => void refetch()} />;
  }

  const myCompanyId = sessionUser?.companyId;
  const isRetracedStaffUser = Boolean(sessionUser?.isRetracedStaff);
  const isShared = !data.isGlobal && data.companyId !== myCompanyId;
  // Global templates are editable only by Retraced staff (matches v1).
  // Ownership and permission are two separate questions; a control needs both.
  // The routes behind these buttons guard on `orders:edit` and `orders:delete`.
  const canEdit = (!data.isGlobal || isRetracedStaffUser) && orderPerms.canEdit;
  const canArchive = !data.isGlobal && !isShared && orderPerms.canDelete;
  const tracingTypeLabel =
    ATTACHMENT_TEMPLATE_TRACING_TYPES[data.tracingType] ?? data.tracingType;

  const handleArchive = async () => {
    try {
      await archiveMutation.mutateAsync(id);
      toast.success(t('detail.archivedSuccess', { name: data.name }));
      await qc.invalidateQueries({
        queryKey: ['orders', 'adminAttachmentTemplates'],
      });
      void navigate({ to: '/orders/attachment-templates' });
    } catch (e) {
      toast.error((e as Error).message);
    }
  };

  return (
    <div className='flex flex-col flex-1 min-h-0 overflow-y-auto'>
      <PageHeader>
        {/* Title row */}
        <div className='flex flex-col gap-1 min-w-0'>
          <h1 className='text-sm font-semibold text-foreground leading-tight'>
            {data.name}
          </h1>
          {data.description && (
            <p className='text-xs text-muted-foreground'>{data.description}</p>
          )}
        </div>

        {/* Footer: tracing-type badge left, actions right */}
        <PageHeaderFooter>
          <PageHeaderFooterLeft>
            <span className='inline-flex items-center px-2 py-0.5 rounded-md bg-background text-xs font-medium'>
              {tracingTypeLabel}
            </span>
          </PageHeaderFooterLeft>
          <PageHeaderFooterRight>
            {canArchive && (
              <>
                <Button
                  variant='outline'
                  onClick={() => setArchiveOpen(true)}
                  disabled={data.hasOrders}
                >
                  <FontAwesomeIcon icon={faBoxArchive} />
                  {t('detail.archive')}
                </Button>
                <AlertDialog open={archiveOpen}>
                  <AlertDialogContent>
                    <AlertDialogHeader>
                      <AlertDialogTitle>
                        {t('detail.archiveTitle')}
                      </AlertDialogTitle>
                      <AlertDialogDescription>
                        {t('detail.archiveDescription', { name: data.name })}
                      </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                      <AlertDialogCancel
                        onClick={() => setArchiveOpen(false)}
                        disabled={archiveMutation.isPending}
                      >
                        {t('detail.archiveCancel')}
                      </AlertDialogCancel>
                      <AlertDialogAction
                        onClick={() => void handleArchive()}
                        loading={archiveMutation.isPending}
                        variant='destructive'
                      >
                        {t('detail.archiveConfirm')}
                      </AlertDialogAction>
                    </AlertDialogFooter>
                  </AlertDialogContent>
                </AlertDialog>
              </>
            )}
            {!isShared && (
              <Button
                variant='outline'
                disabled={!canEdit}
                onClick={() =>
                  canEdit &&
                  void navigate({
                    to: '/orders/attachment-templates/$id/edit',
                    params: { id },
                  })
                }
              >
                <FontAwesomeIcon icon={faPen} />
                {t('detail.edit')}
              </Button>
            )}
          </PageHeaderFooterRight>
        </PageHeaderFooter>
      </PageHeader>

      {/* Rules */}
      <AttachmentTemplateRules
        rules={data.rules}
        backupRule={data.backupRule}
        labels={{
          ruleLabel: n => t('form.ruleLabel', { n }),
          backupSeparator: t('form.backupRuleSeparator'),
          backupRuleTitle: t('form.backupRuleFullTitle'),
          noRules: t('detail.noRules'),
        }}
        fpLabelById={fpLabelById}
        countryNameByCode={countryNameByCode}
      />
    </div>
  );
}
